/**
 * Shared UI primitives — MUI under the PayGO theme (hard rule for ops + field forms).
 * Keep stable export names so domain screens migrate without import churn.
 */
import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary";
};

export function AppButton({
  variant = "primary",
  className = "",
  type = "button",
  children,
  disabled,
  onClick,
  style,
}: ButtonProps) {
  const warning = /\bwarning\b/.test(className);
  return (
    <Button
      type={type}
      variant={variant === "secondary" ? "outlined" : "contained"}
      color={warning ? "warning" : "primary"}
      disabled={disabled}
      onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
      className={className}
      style={style}
      size="medium"
      sx={{
        minHeight: { xs: 44, sm: 40 },
        px: { xs: 2, sm: 2.5 },
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Button>
  );
}

type FieldProps = {
  label: string;
  error?: string;
  children: React.ReactNode;
};

export function AppField({ label, error, children }: FieldProps) {
  return (
    <Box sx={{ mb: 2, width: "100%" }}>
      <Typography
        component="label"
        variant="caption"
        sx={{ display: "block", fontWeight: 600, color: "text.secondary", mb: 0.75 }}
      >
        {label}
      </Typography>
      {children}
      {error ? (
        <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
          {error}
        </Typography>
      ) : null}
    </Box>
  );
}

export function AppInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  const {
    className,
    style,
    type = "text",
    value,
    defaultValue,
    onChange,
    onBlur,
    disabled,
    required,
    id,
    name,
    placeholder,
    autoFocus,
    autoComplete,
    min,
    max,
    step,
    readOnly,
  } = props;
  const controlled = value !== undefined;
  return (
    <TextField
      type={type}
      size="small"
      fullWidth
      className={className}
      style={style}
      value={controlled ? value : undefined}
      defaultValue={!controlled ? defaultValue : undefined}
      onChange={onChange as React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>}
      onBlur={onBlur as React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>}
      disabled={disabled}
      required={required}
      id={id}
      name={name}
      placeholder={placeholder}
      autoFocus={autoFocus}
      autoComplete={autoComplete}
      slotProps={{
        htmlInput: { min, max, step, readOnly },
      }}
      sx={{
        "& .MuiInputBase-root": { minHeight: { xs: 44, sm: 40 } },
      }}
    />
  );
}

export function AppSelect(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  const {
    className,
    style,
    children,
    size: _htmlSize,
    value,
    defaultValue,
    onChange,
    disabled,
    required,
    id,
    name,
    "aria-label": ariaLabel,
  } = props;
  const controlled = value !== undefined;
  return (
    <TextField
      select
      size="small"
      fullWidth
      className={className}
      style={style}
      slotProps={{
        select: { native: true },
        htmlInput: ariaLabel ? { "aria-label": ariaLabel } : undefined,
      }}
      value={controlled ? value : undefined}
      defaultValue={!controlled ? defaultValue : undefined}
      onChange={onChange as unknown as React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>}
      disabled={disabled}
      required={required}
      id={id}
      name={name}
      sx={{
        "& .MuiInputBase-root": { minHeight: { xs: 44, sm: 40 } },
        minWidth: { xs: "100%", sm: 200 },
      }}
    >
      {children}
    </TextField>
  );
}

export function AppTextarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const { className, style, rows = 3, value, defaultValue, onChange, disabled, required, id, name, placeholder } =
    props;
  const controlled = value !== undefined;
  return (
    <TextField
      multiline
      minRows={rows}
      size="small"
      fullWidth
      className={className}
      style={style}
      value={controlled ? value : undefined}
      defaultValue={!controlled ? defaultValue : undefined}
      onChange={onChange as React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>}
      disabled={disabled}
      required={required}
      id={id}
      name={name}
      placeholder={placeholder}
    />
  );
}

export function AppCard({
  title,
  children,
  style,
}: {
  title?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <Card variant="outlined" style={style} sx={{ mb: 2, overflow: "hidden" }}>
      <CardContent sx={{ "&:last-child": { pb: 2 }, p: { xs: 2, sm: 2.5 } }}>
        {title ? (
          <Typography variant="h3" sx={{ mb: 1.5 }}>
            {title}
          </Typography>
        ) : null}
        {children}
      </CardContent>
    </Card>
  );
}

type BadgeTone = "success" | "warning" | "error" | "info" | "neutral";

export function AppBadge({ tone = "neutral", children }: { tone?: BadgeTone; children: React.ReactNode }) {
  const color = tone === "neutral" ? "default" : tone === "info" ? "info" : tone;
  return (
    <Chip
      size="small"
      color={color as "default" | "success" | "warning" | "error" | "info"}
      label={<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>{children}</Box>}
      sx={{ fontWeight: 600, maxWidth: "100%" }}
    />
  );
}

export function AppModal({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        {children}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Second gate for irreversible / high-risk mutates (Cancel / Confirm).
 * Optional type-to-confirm when `confirmPhrase` is set.
 */
export function ConfirmAction({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger = true,
  confirmPhrase,
  busy = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  /** When set, user must type this exact phrase to enable Confirm. */
  confirmPhrase?: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [typed, setTyped] = React.useState("");
  React.useEffect(() => {
    if (open) setTyped("");
  }, [open]);
  const phraseOk = !confirmPhrase || typed.trim() === confirmPhrase;
  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700, color: danger ? "error.main" : "text.primary" }}>
        {title}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: confirmPhrase ? 2 : 0 }}>
          {message}
        </Typography>
        {confirmPhrase ? (
          <TextField
            fullWidth
            size="small"
            label={`Type ${confirmPhrase} to confirm`}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            disabled={busy}
            autoComplete="off"
          />
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <AppButton type="button" variant="secondary" onClick={onCancel} disabled={busy}>
          {cancelLabel}
        </AppButton>
        <AppButton
          type="button"
          onClick={onConfirm}
          disabled={busy || !phraseOk}
          style={danger ? { background: "var(--mui-palette-error-main, #c62828)" } : undefined}
        >
          {busy ? "Working…" : confirmLabel}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
}

/** Static/simple tables only. Interactive listings must use AppDataTable. */
export function AppTable({ head, rows }: { head: string[]; rows: React.ReactNode[][] }) {
  return (
    <TableContainer sx={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {head.map((h) => (
              <TableCell key={h} sx={{ fontWeight: 600, color: "text.secondary", whiteSpace: "nowrap" }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i} hover>
              {r.map((c, j) => (
                <TableCell key={j}>{c}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function AppAlert({
  severity = "info",
  children,
}: {
  severity?: "success" | "warning" | "error" | "info";
  children: React.ReactNode;
}) {
  return (
    <Alert severity={severity} sx={{ mb: 2 }} role="alert">
      {children}
    </Alert>
  );
}

/** Blocking form-error dialog (GreenLeaf G1-01 — popup alert on submit failure). */
export function AppFormAlert({
  open,
  title = "Something went wrong",
  message,
  onClose,
}: {
  open: boolean;
  title?: string;
  message: string;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Alert severity="error" role="alert">
          {message}
        </Alert>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <AppButton type="button" onClick={onClose}>
          OK
        </AppButton>
      </DialogActions>
    </Dialog>
  );
}
